import { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { H1 } from "@components";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import type { Report } from "../types";

export const ReportTable = ({ report }: { report?: Report }) => {
  if (report === undefined) {
    return null;
  }

  const { commonStatement, financialStatement } = report.content;

  const translatorsEntries = Object.entries(financialStatement?.Summary ?? {});
  const projectsEntries = Object.entries(financialStatement?.Projects ?? {});
  const commonStatementEntries = Object.entries(commonStatement?.Projects ?? {});

  const onTitleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.parentElement?.classList.toggle("table-tbody--collapsed");
  };

  const onFolderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.parentElement?.classList.toggle("report-folder--collapsed");
  };

  return (
    <Box>
      <div className="table-report__separator" />

      <H1 label="Projects" />
      {projectsEntries.length === 0 && <p>No data.</p>}

      {translatorsEntries.length === 0 ? null : (
        <div>
          <div className="report-project__title" onClick={onFolderClick} aria-hidden="true">
            <ChevronRightIcon className="table-report__toggler" width={18} height={18} />
            <Typography variant="h3" gutterBottom>
              All Projects
            </Typography>
          </div>
          <table className="table-report">
            <thead>
              <tr>
                <th>Translator</th>
                <th>Changed Words</th>
                <th>New Words</th>
                <th>Total</th>
              </tr>
            </thead>

            {translatorsEntries.map(([label, data], index) => (
              <Fragment key={label}>
                <tbody className="table-tbody--collapsed">
                  <tr
                    className={`table-report__label table-report__label-hover ${index % 2 ? "table-report__label--odd" : ""}`}
                    onClick={onTitleClick}
                  >
                    <td>
                      <ChevronRightIcon className="table-report__toggler" width={14} height={14} />
                      {label}
                    </td>
                    <td>{data.TranslatorSummary.ChangedWords}</td>
                    <td>{data.TranslatorSummary.NewWords}</td>
                    <td>{data.TranslatorSummary.Total}</td>
                  </tr>
                </tbody>
                <tbody>
                  {data.Languages.map(language => (
                    <tr key={language.Language}>
                      <td>{language.Language}</td>
                      <td>{language.ChangedWords}</td>
                      <td>{language.NewWords}</td>
                      <td>{language.Total}</td>
                    </tr>
                  ))}
                </tbody>
              </Fragment>
            ))}

            <tbody>
              <tr className="table-report__summary">
                <td>Total:</td>
                <td>{translatorsEntries.reduce((acc, [, data]) => acc + data.TranslatorSummary.ChangedWords, 0)}</td>
                <td>{translatorsEntries.reduce((acc, [, data]) => acc + data.TranslatorSummary.NewWords, 0)}</td>
                <td>{translatorsEntries.reduce((acc, [, data]) => acc + data.TranslatorSummary.Total, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {projectsEntries.map(([label, data]) => (
        <div key={label} className="report-folder--collapsed">
          <div className="table-report__separator-project" />

          <div className="report-project__title" onClick={onFolderClick} aria-hidden="true">
            <ChevronRightIcon className="table-report__toggler" width={18} height={18} />
            <Typography variant="h3" gutterBottom>
              {label}
            </Typography>
          </div>
          <table className="table-report">
            <thead>
              <tr>
                <th>Translator</th>
                <th>Changed Words</th>
                <th>New Words</th>
                <th>Total</th>
              </tr>
            </thead>

            {data.Translators.map((translator, index) => (
              <Fragment key={translator.Name}>
                <tbody className="table-tbody--collapsed">
                  <tr
                    className={`table-report__label table-report__label-hover ${index % 2 ? "table-report__label--odd" : ""}`}
                    onClick={onTitleClick}
                  >
                    <td>
                      <ChevronRightIcon className="table-report__toggler" width={14} height={14} />
                      {translator.Name}
                    </td>
                    <td>{translator.TranslatorSummary.ChangedWords}</td>
                    <td>{translator.TranslatorSummary.NewWords}</td>
                    <td>{translator.TranslatorSummary.Total}</td>
                  </tr>
                </tbody>
                <tbody>
                  {translator.Languages.map(language => (
                    <Fragment key={language.Language}>
                      <tr>
                        <td>{language.Language}</td>
                        <td>{language.ChangedWords}</td>
                        <td>{language.NewWords}</td>
                        <td>{language.Total}</td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </Fragment>
            ))}

            <tbody>
              <tr className="table-report__summary">
                <td>Total:</td>
                <td>
                  {data.Translators.reduce((acc, translator) => acc + translator.TranslatorSummary.ChangedWords, 0)}
                </td>
                <td>{data.Translators.reduce((acc, translator) => acc + translator.TranslatorSummary.NewWords, 0)}</td>
                <td>{data.Translators.reduce((acc, translator) => acc + translator.TranslatorSummary.Total, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      <div className="table-report__separator" />

      <H1 label="Summary" />

      <table className="table-report">
        <thead>
          <tr>
            <th>Project</th>
            <th>Approved Items</th>
            <th>Total Items</th>
            <th>Approved Words</th>
            <th>Unapproved Words</th>
            <th>Total Words</th>
          </tr>
        </thead>
        <tbody>
          {commonStatementEntries.map(([label, data], index) => (
            <Fragment key={label}>
              <tr className={`table-report__label ${index % 2 ? "table-report__label--odd" : ""}`}>
                <td>{label}</td>
                <td>{data.NumberOfApprovedItems}</td>
                <td>{data.TotalNumberOfItems}</td>
                <td>{data.NumberOfApprovedWords}</td>
                <td>{data.TotalNumberOfWords - data.NumberOfApprovedWords}</td>
                <td>{data.TotalNumberOfWords}</td>
              </tr>
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className="table-report__summary">
            <td>Total:</td>
            <td>{commonStatement.Summary.NumberOfApprovedItems}</td>
            <td>{commonStatement.Summary.TotalNumberOfItems}</td>
            <td>{commonStatement.Summary.NumberOfApprovedWords}</td>
            <td>{commonStatement.Summary.TotalNumberOfWords - commonStatement.Summary.NumberOfApprovedWords}</td>
            <td>{commonStatement.Summary.TotalNumberOfWords}</td>
          </tr>
        </tfoot>
      </table>
    </Box>
  );
};
